import { useCFPValues } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { useDidMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { Box, ComposableTypography, Link } from '@bcp-nextgen-dx-component-factory/design-system';
import { ArrowRight } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { AppComposableTypography, CmdIcon } from '@components';
import { BaseConfig } from '@configs';
import styled from '@emotion/styled';
import { AmaError, AmaHelper, AppHelper, LoggerHelper, UIHelper } from '@helpers';
import { useLoader, useModal, useTranslation } from '@hooks';
import { useFetchAccountCmdUrl } from '@services';
import { useAmaStore, useSearchParamsStore } from '@store';
import { endpoints, SUBTITLE_100_04 } from '@types';

const resources = {
	title: 'screens.login.form.cmd.title',
	link: {
		text: 'screens.login.form.cmd.link.text',
		url: 'screens.login.form.cmd.link.url',
	},
	button: 'screens.login.form.cmd.button',
};

const CMD_HEX_COLOR = '#3453b4';

interface CmdBlockProps {
	isVisible?: boolean;
}

export const CmdBlock = ({ isVisible }: CmdBlockProps) => {
	const { t } = useTranslation();
	const { getToken, getSpacing } = useCFPValues();

	const globalLoader = useLoader('global');
	const genericErrorModal = useModal('genericError');

	const language = useSearchParamsStore((state) => state.language);
	const setAmaState = useAmaStore((state) => state.setState);

	const { fetch: fetchAccountCmdUrl } = useFetchAccountCmdUrl();

	useDidMount(() => {
		handleAmaRedirect();
	});

	const handleAmaRedirect = () => {
		const { isAmaRedirect, hashParams } = getAmaNavigation();

		if (isAmaRedirect) {
			LoggerHelper.log('App', 'Cmd > handleAmaRedirect:', { isAmaRedirect, hashParams });

			setAmaState({ isAmaRedirect, hashParams });
			AmaHelper.clearHashParams();

			if (hashParams.error === AmaError.cancelled) {
				// user cancelled the authentication, do nothing
				return;
			}

			if (hashParams.error === AmaError.invalidRequest || !hashParams.accessToken) {
				// something went wrong with AMA, show error modal
				genericErrorModal.show();
				return;
			}

			globalLoader.show();

			const { accessToken, tokenType, expiresIn, refreshToken, state } = hashParams;

			AppHelper.submitForm(endpoints.accountCmdAuthentication, {
				accessToken,
				tokenType,
				expiresIn,
				refreshToken,
				state,
			});
		}
	};

	const handleButtonClick = async () => {
		try {
			globalLoader.show();

			const res = await fetchAccountCmdUrl({ language: language?.toUpperCase() || 'PT' });

			if (!res.data?.url) {
				throw new Error('URL not provided from fetchAccountCmdUrl');
			}

			const { baseUrl: amaBaseUrl, searchParams } = decodeSearchParams(res.data.url);
			const { client_id, redirect_uri, response_type: responseType, scope, state } = searchParams || {};

			if (!amaBaseUrl || !client_id || !redirect_uri || !responseType || !scope) {
				// no valid response from service, throw error
				throw new Error('Missing params in URL from fetchAccountCmdUrl');
			}

			let baseUrl = amaBaseUrl;
			let clientId = client_id;
			let redirectUri = redirect_uri;
			// when localhost, use the dev client id and redirect uri
			if (BaseConfig.environment === 'development') {
				clientId = '6943699124927193864';
				redirectUri = 'http://dev.millenniumbcp.pt:3000/connect/cmdAuthentication';

				if (BaseConfig.mockStart) {
					baseUrl = 'https://cmbofe.adm-ch-wbo-cmbo.dv.mbcp.cloud/utils/ama-emulator';
					redirectUri = `${window.location.origin}/connect/cmdAuthentication`;
				}
			}

			AmaHelper.redirect({
				data: {
					baseUrl,
					clientId,
					redirectUri,
					scope,
					responseType,
					state,
				},
				options: {
					storage: 'session',
				},
			});
		} catch {
			globalLoader.hide();
			genericErrorModal.show();
		}
	};

	const decodeSearchParams = (url?: string | null): { baseUrl?: string; searchParams?: Record<string, string> } => {
		if (!url) {
			return {};
		}

		const paramsObj: Record<string, string> = {};

		// Create a URL object
		const urlObj = new URL(url);

		// Iterate through all search parameters
		urlObj.searchParams.forEach((value, key) => {
			// Decode the parameter value
			paramsObj[key] = decodeURIComponent(value);
		});

		return {
			baseUrl: urlObj.origin + urlObj.pathname,
			searchParams: paramsObj,
		};
	};

	const getAmaNavigation = () => {
		const isAmaRedirect = AmaHelper.isAmaRedirect();
		const hashParams = AmaHelper.getHashParams();

		return { isAmaRedirect, hashParams };
	};

	if (!isVisible) {
		return null;
	}

	const link = {
		text: t(resources.link.text),
		url: t(resources.link.url),
	};

	const showLink = link.text && link.text !== resources.link.text && link.url && link.url !== resources.link.url;

	return (
		<Box
			styles={{
				flexDirection: 'column',
				gap: getSpacing({
					default: {
						default: 24,
						mobile: 24,
						tablet: 24,
						laptop: 40,
					},
				}),
			}}
		>
			<Box
				styles={{
					flexDirection: 'column',
					gap: UIHelper.rem(8),
				}}
			>
				<AppComposableTypography
					appearance={getToken({
						default: {
							default: SUBTITLE_100_04,
							mobile: SUBTITLE_100_04,
							tablet: SUBTITLE_100_04,
							laptop: 'subtitle-200-02',
						},
					})}
					text={t(resources.title)}
				/>

				{showLink && (
					<Link
						label={link.text}
						link={link.url}
						rightIcon={ArrowRight.Outline}
						target="_blank"
						size="large"
					/>
				)}
			</Box>

			<MobileDigitalKeyButton onClick={handleButtonClick}>
				<CmdIcon fill={CMD_HEX_COLOR} />
				<ComposableTypography text={t(resources.button)} baseProps={{ appearance: 'body-100-03' }} tags={{}} />
			</MobileDigitalKeyButton>
		</Box>
	);
};

const MobileDigitalKeyButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 48px;
	width: fit-content;
	padding: 0 24px;
	gap: 8px;
	border: 2px solid ${CMD_HEX_COLOR};
	border-radius: 100px;
	cursor: pointer;

	span {
		color: ${CMD_HEX_COLOR};
	}

	:hover {
		background: ${CMD_HEX_COLOR};

		span {
			color: #fff;
		}

		svg,
		path {
			fill: #fff;
		}
	}

	:focus-visible {
		outline-width: 2px;
		outline-style: solid;
		outline-color: #12161a;
		outline-offset: 2px;
	}
`;
